const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class APIError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

const api = {
  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const token = localStorage.getItem('auth-token');

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        localStorage.removeItem('auth-token');
        window.location.href = '/';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new APIError(errorData.error || errorData.message || 'Error en la solicitud', response.status);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError('Error de conexión', 0);
    }
  },

  get(endpoint) {
    return this.request(endpoint);
  },

  post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  },

  // Auth endpoints
  auth: {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    verify: () => api.get('/auth/verify'),
    changePassword: (data) => api.post('/auth/change-password', data),
  },

  // User endpoints
  users: {
    getProfile: () => api.get('/users/profile'),
    updateProfile: (data) => api.put('/users/profile', data),
    getUsers: () => api.get('/users'),
    updateUser: (id, data) => api.put(`/users/${id}`, data),
    deleteUser: (id) => api.delete(`/users/${id}`),
  },

  // Events endpoints
  events: {
    getAll: () => api.get('/events'),
    getById: (id) => api.get(`/events/${id}`),
    create: (data) => api.post('/events', data),
    update: (id, data) => api.put(`/events/${id}`, data),
    delete: (id) => api.delete(`/events/${id}`),
  },

  // Betting endpoints
  betting: {
    placeBet: (data) => api.post('/betting/place', data),
    getUserBets: () => api.get('/betting/user'),
    getAllBets: () => api.get('/betting/admin'),
    updateBet: (id, data) => api.put(`/betting/${id}`, data),
    deleteBet: (id) => api.delete(`/betting/${id}`),
  },

  // Transactions endpoints
  transactions: {
    getUser: () => api.get('/transactions/user'),
    getAll: () => api.get('/transactions'),
    create: (data) => api.post('/transactions', data),
  },

  // Dashboard endpoints
  dashboard: {
    getAdminStats: () => api.get('/dashboard/admin'),
    getClientStats: () => api.get('/dashboard/client'),
  },

  // Settings endpoints
  settings: {
    getAll: () => api.get('/settings'),
    getPublic: () => api.get('/settings/public'),
    getByKey: (key) => api.get(`/settings/${key}`),
    update: (settings) => api.put('/settings', { settings }),
    updateByKey: (key, value) => api.put(`/settings/${key}`, { value }),
  },
};

export default api;
export { APIError };